'use client'

import clsx from 'clsx'

import { ButtonSizes, ButtonVariants } from 'shared/ui/Button/Button.enums'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import { cssColors } from 'shared/ui/utils/cssTokens'

import styles from './Button.module.scss'

export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode
  variant?: ButtonVariants
  size?: ButtonSizes
  disabled?: boolean
  startIcon?: React.ReactElement
  endIcon?: React.ReactElement
  fullWidth?: boolean
  type?: 'submit' | 'button'
  loading?: boolean
  active?: boolean
  ref?: React.Ref<HTMLButtonElement>
}
const Button = ({
  fullWidth,
  onClick,
  children,
  variant = ButtonVariants.primary,
  size = ButtonSizes.medium,
  disabled,
  startIcon,
  endIcon,
  loading,
  active = false,
  ref,
  className,
  ...rest
}: ButtonProps) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(event)
    }
  }

  const showLoaderOnStart = loading && startIcon
  const showLoaderOnEnd = loading && endIcon && !startIcon
  const showLoaderOnMiddle = loading && !startIcon && !endIcon

  return (
    <button
      {...rest}
      className={clsx(
        styles.button,
        styles[variant] ?? styles.primary,
        { [styles.fullWidth]: fullWidth, [styles.active]: active },
        className
      )}
      data-size={size as string}
      disabled={disabled}
      onClick={disabled ? () => {} : handleClick}
      aria-busy={loading}
      ref={ref}
    >
      {startIcon && !showLoaderOnStart && (
        <div className={styles.iconContainer}>{startIcon}</div>
      )}
      {showLoaderOnStart && (
        <div className={styles.iconContainer}>
          <LoadingIndicator color={cssColors.brand.tertiary} size={20} />
        </div>
      )}
      <span
        className={clsx(styles.content, {
          [styles.contentHidden]: showLoaderOnMiddle
        })}
      >
        {children}
      </span>
      {showLoaderOnMiddle && (
        <div className={styles.middleIconContainer}>
          <LoadingIndicator color={cssColors.brand.tertiary} size={20} />
        </div>
      )}
      {endIcon && !showLoaderOnEnd && (
        <div className={styles.iconContainer}>{endIcon}</div>
      )}
      {showLoaderOnEnd && (
        <div className={styles.iconContainer}>
          <LoadingIndicator color={cssColors.brand.tertiary} size={20} />
        </div>
      )}
    </button>
  )
}

export default Button
